import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress } from "@mui/material";
import { getAuthHeaderProvider } from "@src/appV2/Auth/cognito/authHeaderProviderRef";
import { environmentConfig } from "@src/appV2/environment";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { invalidateBookabilityStatus } from "@src/appV2/OpenShifts/ShiftAction/api/useBookabilityStatus";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { useCreateBackgroundCheckConsent } from "./apis/useCreateBackgroundCheckConsent";
import { useGetBackgroundCheckConsentConfigurations } from "./apis/useGetBackgroundCheckConsentConfigurations";
import { BackgroundCheckPageContainer } from "./BackgroundCheckPageContainer";
import { useCheckrScript } from "./useCheckrSdk";

interface BackgroundCheckConsentPageProps {
  onContinue: () => void;
  onBack: () => void;
  returnToIntroPage: () => void;
}

function getSessionTokenPath(): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/checkr/session-tokens`;
}

const CHECKR_CONTAINER_TAG = "checkr-disclosure-container";

export function BackgroundCheckConsentPage(props: BackgroundCheckConsentPageProps) {
  const { onContinue, onBack, returnToIntroPage } = props;

  const queryClient = useQueryClient();
  const worker = useDefinedWorker();
  const { showErrorToast } = useToast();
  const {
    isLoading: isLoadingBackgroundCheckConsentConfigurations,
    isSuccess: isSuccessBackgroundCheckConsentConfigurations,
    data: backgroundCheckConsentConfigurations,
    isError: isErrorBackgroundCheckConsentConfigurations,
  } = useGetBackgroundCheckConsentConfigurations({
    workerId: worker.userId,
  });
  const { mutateAsync: createBackgroundCheckConsent } = useCreateBackgroundCheckConsent({
    onError: (error) => {
      logError(APP_V2_APP_EVENTS.POST_BACKGROUND_CHECK_CONSENT_FAILURE, { error });
      showErrorToast(
        "Something went wrong while recording your consent. Please try again later, or contact support!"
      );
    },
  });
  const {
    isLoading: isLoadingCheckrScript,
    isError: isErrorCheckrScript,
    Checkr,
  } = useCheckrScript();
  const embeddedRef = useRef<boolean>(false);

  const isLoading = isLoadingBackgroundCheckConsentConfigurations || isLoadingCheckrScript;
  const isError = isErrorBackgroundCheckConsentConfigurations || isErrorCheckrScript;
  const backgroundCheckConsentConfiguration = backgroundCheckConsentConfigurations?.data[0];

  useEffect(() => {
    if (
      (isSuccessBackgroundCheckConsentConfigurations &&
        !isDefined(backgroundCheckConsentConfiguration)) ||
      isErrorBackgroundCheckConsentConfigurations
    ) {
      logError(APP_V2_APP_EVENTS.BACKGROUND_CHECK_CONSENT_CONFIGURATIONS_ERROR, {
        error: new Error("No background check consent configuration found"),
        metadata: {
          workerId: worker.userId,
          isSuccessBackgroundCheckConsentConfigurations,
          isErrorBackgroundCheckConsentConfigurations,
          backgroundCheckConsentConfiguration,
        },
      });
      showErrorToast(
        "Something went wrong when loading disclosures. Please try again, or contact support!"
      );
      returnToIntroPage();
    }
  }, [
    isSuccessBackgroundCheckConsentConfigurations,
    backgroundCheckConsentConfiguration,
    returnToIntroPage,
    isErrorBackgroundCheckConsentConfigurations,
    worker.userId,
    showErrorToast,
  ]);

  useEffect(() => {
    async function openCheckrFlow() {
      if (!Checkr || embeddedRef.current || !backgroundCheckConsentConfiguration) {
        return;
      }

      embeddedRef.current = true;
      const authHeaderValue = await getAuthHeaderProvider()?.();
      if (!authHeaderValue) {
        logError(APP_V2_APP_EVENTS.FAILED_TO_GET_AUTH_TOKEN_FOR_BACKGROUND_CHECK_CONSENT, {
          error: new Error("No authorization header value found"),
          metadata: { workerId: worker.userId },
        });
        showErrorToast("Something went wrong. Please try again later, or contact support!");
        return;
      }

      // Create the Disclosure & Consent embed
      const embed = new Checkr.Embeds.DisclosureConsent({
        sessionTokenPath: getSessionTokenPath(),
        sessionTokenRequestHeaders: () => ({ Authorization: authHeaderValue }),
        accountName: backgroundCheckConsentConfiguration.attributes.accountName,
        candidateFullName: backgroundCheckConsentConfiguration.attributes.fullName,
        candidateId: backgroundCheckConsentConfiguration.attributes.candidateId,
        language: "en",
        packageSlug: backgroundCheckConsentConfiguration.attributes.packageSlug,
        workLocationState: backgroundCheckConsentConfiguration.attributes.workLocationState,
        env: backgroundCheckConsentConfiguration.attributes.environmentName,
        onConsentSubmitSuccess: async () => {
          logEvent(APP_V2_APP_EVENTS.BACKGROUND_CHECK_CONSENT_SUBMITTED);
          try {
            await createBackgroundCheckConsent({
              workerId: worker.userId,
              candidateId: backgroundCheckConsentConfiguration.attributes.candidateId,
            });
            await invalidateBookabilityStatus(queryClient);
            onContinue();
          } catch {
            // Error already logged and toast shown by mutation's onError
          }
        },
        onConsentSubmitError: (maybeError: unknown) => {
          const error = maybeError instanceof Error ? maybeError : new Error(String(maybeError));
          logError(APP_V2_APP_EVENTS.BACKGROUND_CHECK_CONSENT_SUBMIT_ERROR, { error });
          showErrorToast(
            "Something went wrong while submitting your consent. Please try again later, or contact support!"
          );
        },
        onLoadDisclosureError: (maybeError: unknown) => {
          const error = maybeError instanceof Error ? maybeError : new Error(String(maybeError));
          logError(APP_V2_APP_EVENTS.BACKGROUND_CHECK_CONSENT_LOAD_ERROR, { error });
          showErrorToast(
            "Something went wrong while loading the disclosures. Please try again later, or contact support!"
          );
        },
      });

      embed.render(`#${CHECKR_CONTAINER_TAG}`);
    }

    void openCheckrFlow();
  }, [
    Checkr,
    backgroundCheckConsentConfiguration,
    onContinue,
    createBackgroundCheckConsent,
    showErrorToast,
    worker.userId,
    queryClient,
  ]);

  useLogEffect(APP_V2_APP_EVENTS.BACKGROUND_CHECK_CONSENT_PAGE_VIEWED);

  return (
    <BackgroundCheckPageContainer hideFooter navigateBack={onBack}>
      {isLoading && !isError && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            height: "100%",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            height: "100%",
          }}
        >
          <Text color="error">
            Failed to load disclosures. Please try again later or contact support if the issue
            persists.
          </Text>
        </Box>
      )}

      {!isLoading && !isError && (
        <Box
          id={CHECKR_CONTAINER_TAG}
          data-testid={CHECKR_CONTAINER_TAG}
          sx={{
            width: "100%",
            height: "100%",
            maxHeight: "100%",
          }}
        />
      )}
    </BackgroundCheckPageContainer>
  );
}
